import { useState } from 'react';
import { RichTextEditor } from '@/components/RichTextEditor';
import type { OperatorTemplateRow, SaveOperatorTemplatePayload } from '@/api/operatorTemplates';

/** 1:1 текст help() у Quill::make('template.text') в старом Orchid OperatorEditLayout.php. */
const VARIABLES: { code: string; description: string }[] = [
  { code: '%RESPONDER%', description: 'респондент' },
  { code: '%NAME%', description: 'наименование организации' },
  { code: '%BIN%', description: 'ОГРН' },
  { code: '%ADDRESS%', description: 'юридический адрес' },
  { code: '%SERVICE%', description: 'система' },
  { code: '%URL%', description: 'URL сайта' },
  { code: '%DATE%', description: 'дата предоставления' },
];

/**
 * Форма шаблона — используется и для создания (initial не задан), и для
 * редактирования (initial = текущий шаблон), 1:1 поля OperatorEditLayout
 * (Orchid): name/description/default/text. Валидация — те же правила, что
 * request->validate() в OperatorTemplateController (name/text required) —
 * здесь только для мгновенной обратной связи, сервер проверяет заново.
 */
export function OperatorTemplateForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: OperatorTemplateRow;
  onSave: (payload: SaveOperatorTemplatePayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [text, setText] = useState(initial?.text ?? '');
  const [isDefault, setIsDefault] = useState(initial?.default ?? false);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  const submit = async () => {
    const missing: string[] = [];
    if (!name.trim()) missing.push('name');
    if (!text.trim()) missing.push('text');
    setFieldErrors(missing);
    if (missing.length > 0) {
      setError('Заполните обязательные поля.');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim() || null, text, default: isDefault });
    } catch {
      setError('Не удалось сохранить шаблон.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`operator-template-form${saving ? ' operator-template-form--saving' : ''}`} aria-busy={saving}>
      {error && <p className="field-error">{error}</p>}

      <div className="edit-fields">
        <label className={`edit-field${invalid('name')}`}>
          <span>Наименование *</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
        </label>

        <label className="edit-field">
          <span>Описание</span>
          <input type="text" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} disabled={saving} />
        </label>

        <label className="edit-field edit-field--checkbox edit-field--full">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} disabled={saving} />
          <span>Шаблон по умолчанию</span>
        </label>
        <p className="edit-field__hint edit-field__hint--oneline">
          При установке шаблон будет использоваться по умолчанию для всех клиентов, у которых шаблон не определён.
        </p>

        {/* Не <label>: <label> без for= пересылает клик на ПЕРВЫЙ labelable-потомок,
            а это кнопка «Жирный» в тулбаре — клик по тексту в редакторе применял
            жирность и не давал снять выделение. Подпись связываем через aria. */}
        <div className={`edit-field edit-field--full${invalid('text')}`}>
          <span id="operator-template-text-label">Текст документа *</span>
          <RichTextEditor
            value={text}
            onChange={setText}
            disabled={saving}
            ariaLabelledBy="operator-template-text-label"
          />
        </div>

        <div className="operator-template-form__variables">
          <p className="edit-field__hint">Используйте переменные внутри текста:</p>
          <ul className="operator-template-form__variables-list">
            {VARIABLES.map((v) => (
              <li key={v.code}>
                <code>{v.code}</code> — {v.description}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="operator-template-form__actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
          Отмена
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
          Сохранить
        </button>
      </div>
    </div>
  );
}
